/*
 * Schema Field Limits
 *
 * Applies feature-owned limits to schema fields before shared forms render them.
 * Prospect Contact Note/Notes textareas use the shared 65,536-character Note cap while
 * preserving stricter positive schema limits and leaving other fields unchanged.
 */

import type { Schema } from '../context/SchemaContext';
import { NOTE_MAX_LENGTH } from '../constants/noteConstants';

export const applyProspectContactNoteLimit = <T extends Schema>(schema: T): T => {
  try {
    const fields = JSON.parse(schema.schema);
    if (!Array.isArray(fields)) return schema;

    const limitedFields = fields.map(field => {
      const normalizedName = String(field?.name ?? '').trim().toLowerCase();
      const normalizedLabel = String(field?.label ?? '').trim().toLowerCase();
      const isNoteTextarea = field?.type === 'textarea'
        && (['note', 'notes'].includes(normalizedName) || ['note', 'notes'].includes(normalizedLabel));

      if (!isNoteTextarea) return field;

      const configuredMaximum = Number(field.maxLength);
      const maxLength = Number.isFinite(configuredMaximum) && configuredMaximum > 0
        ? Math.min(configuredMaximum, NOTE_MAX_LENGTH)
        : NOTE_MAX_LENGTH;

      return { ...field, maxLength };
    });

    return { ...schema, schema: JSON.stringify(limitedFields) } as T;
  } catch {
    return schema;
  }
};
